import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ConfigContext } from "../../../Context/ConfigContext";
import PageTitle from "../../../Components/PageTitle";
import Swal from "sweetalert2";
import { OverlayLoader } from "../../../Components/Shimmers";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { axiosGet } from "../../../Apis/axiosMethods";

const PrintStyles = () => (
    <style>
        {`
        @page {
            size: A4;
            margin: 0;
        }

        @media print {
            body {
                margin: 0 !important;
                padding: 0 !important;
                background: #fff !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }

            .print-hide {
                display: none !important;
            }

            .credit-note-page {
                width: 250mm !important;
                min-height: 297mm !important;
                margin: 0 !important;
                padding: 12mm !important;
                box-shadow: none !important;
                border: none !important;
                page-break-after: always;
            }
        }

        .credit-note-page {
            width: 250mm;
            min-height: 297mm;
            margin: 0 auto;
            padding: 12mm;
            background: #fff;
            font-family: Arial, sans-serif;
            color: #000;
            box-sizing: border-box;
        }

        .cn-box {
            border: 2px solid #000;
            border-radius: 10px;
            background: #fff;
            padding: 12px 14px;
            position: relative;
        }

        .cn-badge {
            display: inline-block;
            padding: 4px 10px;
            border: 2px solid #000;
            border-radius: 10px;
            font-weight: 700;
            font-size: 12px;
            background: #fff;
            position: absolute;
            top: -14px;
            left: 12px;
        }

        .cn-title {
            font-size: 28px;
            font-weight: 700;
            text-align: center;
            margin-top: 18px;
            margin-bottom: 4px;
        }

        .cn-trn {
            text-align: center;
            font-weight: 700;
            font-size: 13px;
            margin-bottom: 10px;
        }

        .cn-divider {
            border-top: 2px solid #000;
            margin-top: 12px;
            margin-bottom: 0;
        }

        .cn-table thead th {
            border-top: 2px solid #000 !important;
            border-bottom: 2px solid #000 !important;
            text-transform: uppercase;
            font-weight: 700;
            font-size: 12px;
            padding: 8px 6px;
            vertical-align: middle;
        }

        .cn-table tbody td {
            border-bottom: 1px solid #ddd !important;
            font-size: 12px;
            padding: 10px 6px;
            vertical-align: top;
        }

        .cn-bottom-divider {
            border-top: 2px solid #000;
            margin-top: 14px;
        }

        .cn-footer {
            font-size: 11px;
            color: #000;
        }
    `}
    </style>
);

const PreviewCreditNote = () => {
    const { apiHeaderJson, apiB2BURL } = useContext(ConfigContext);
    const headers = apiHeaderJson;

    const { voucher_id } = useParams();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const getInfo = async () => {
        try {
            setLoading(true);
            const response = await axiosGet(`/Users/GetCreditNoteInfo`, { voucher_id });

            const { success, data } = response.data;
            if (success) setData(data);
            else setData(null);
        } catch (error) {
            console.error(error);
            Swal.fire("Error!", "Failed to load credit note information", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getInfo();
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }, [voucher_id]);

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || amount === "") return "0.00";
        return Number(amount).toFixed(2);
    };

    const displayGrandTotal = Number(data?.credit_note?.business_return_total_refund_amount || 0);
    const vatAmount = Number(data?.credit_note?.business_return_vat_amount || 0);

    const taxableAmount = displayGrandTotal - vatAmount;

    const handleDownloadPDF = () => {
        const doc = new jsPDF('p', 'mm', 'a4');

        const content = document.querySelector('.credit-note-page');

        doc.html(content, {
            callback: function (doc) {
                doc.save(`Credit_Note_${voucher_id}.pdf`);
            },
            x: 5,
            y: 5,
            width: 200,
            windowWidth: 1000
        });
    };


    return (
        <>
            {
                loading ?
                    <div style={{ height: "70vh" }}>
                        <OverlayLoader />
                    </div>
                    :
                    <main style={{ marginTop: '-10px' }}>
                        <PrintStyles />

                        <div className="print-hide">
                            <PageTitle title="Preview Credit Note" primary="My Account" />
                        </div>

                        <div className="row mb-3 print-hide mt-3">
                            <div className="col-md-8"></div>
                            <div className="col-md-4 d-flex gap-2 justify-content-start">
                                <button className="btn btn-primary" onClick={handleDownloadPDF}>
                                    <i className="sicon-cloud-download" style={{ color: '#FFF', fontSize: '1.6rem' }}></i> Download PDF
                                </button>
                            </div>
                        </div>

                        {!data ? (
                            <div className="container print-hide">
                                <div className="alert alert-danger">No data found for this credit note.</div>
                            </div>
                        ) : (
                            <div className="credit-note-page border mb-3">
                                {/* Header */}
                                <div className="d-flex align-items-center justify-content-between pb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <img
                                            src="/assets/images/Logo/logo_dark.png"
                                            alt="Company Logo"
                                            className="d-block"
                                            style={{ width: "190px", height: "60px", objectFit: "contain" }}
                                        />
                                    </div>

                                    <div className="text-end">
                                        <h3 className="m-0 fw-bold" style={{ fontSize: "20px" }}>
                                            Ghayar Auto Spare Parts Trading LLC
                                        </h3>
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="cn-title">Tax Credit Note</div>
                                <div className="cn-trn">TRN: 100442212500003</div>

                                {/* Customer + Right Box */}
                                <div className="row g-3 mt-2">
                                    <div className="col-7">
                                        <div className="cn-box">
                                            <div className="cn-badge bg-light">CUSTOMER DETAILS</div>

                                            <div className="mt-3">
                                                <div className="fw-bold" style={{ fontSize: "14px" }}>
                                                    {data.customer_details?.business_name || "N/A"}
                                                </div>

                                                <div className="small mt-1" style={{ fontSize: "12px" }}>
                                                    ##Tel {data.customer_details?.business_contact_number || "N/A"} Fax
                                                </div>

                                                <div style={{ height: "18px" }} />

                                                <div className="small mb-1" style={{ fontSize: "12px" }}>
                                                    <span className="fw-bold d-inline-block" style={{ width: "120px" }}>
                                                        CLIENT TRN:
                                                    </span>
                                                    {data.customer_details?.busienss_trn || "N/A"}
                                                </div>

                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-5">
                                        <div className="cn-box">
                                            <div className="small mb-1" style={{ fontSize: "12px" }}>
                                                <span className="fw-bold d-inline-block" style={{ width: "90px" }}>
                                                    SRT NO
                                                </span>
                                                <span className="fw-bold">{data.credit_note?.business_return_id || "N/A"}</span>
                                            </div>

                                            <div className="small mb-1" style={{ fontSize: "12px" }}>
                                                <span className="fw-bold d-inline-block" style={{ width: "90px" }}>
                                                    SRT DATE
                                                </span>
                                                <span>{formatDate(data.credit_note?.business_return_requested_datetime)}</span>
                                            </div>

                                            <div className="small mb-1" style={{ fontSize: "12px" }}>
                                                <span className="fw-bold d-inline-block" style={{ width: "90px" }}>
                                                    SALESMAN
                                                </span>
                                                <span>{data.customer_details?.business_salesmen_name || "N/A"}</span>
                                            </div>

                                            <div className="small mb-1" style={{ fontSize: "12px" }}>
                                                <span className="fw-bold d-inline-block" style={{ width: "90px" }}>
                                                    Voucher No
                                                </span>
                                                <span>{data?.voucher?.voucher_id || "N/A"}</span>
                                            </div>

                                            <div className="small mb-1" style={{ fontSize: "12px" }}>
                                                <span className="fw-bold d-inline-block" style={{ width: "90px" }}>
                                                    Order No
                                                </span>
                                                <span className="fw-bold">{data.credit_note?.secret_order_id || "N/A"}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="cn-divider" />

                                {/* Items Table */}
                                <div className="mt-2">
                                    <table className="table table-borderless mb-0 cn-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "70px" }}>SL No</th>
                                                <th style={{ width: "170px" }}>PART NUMBER</th>
                                                <th>PART NAME</th>
                                                <th className="text-center" style={{ width: "70px" }}>
                                                    QTY
                                                </th>
                                                <th className="text-end" style={{ width: "120px" }}>
                                                    UNIT PRICE
                                                </th>
                                                <th className="text-end" style={{ width: "120px" }}>
                                                    VAT 5%
                                                </th>
                                                <th className="text-end" style={{ width: "120px" }}>
                                                    AMOUNT
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {data?.return_items?.map((item, index) => (
                                                <tr key={item.business_return_item_id || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{item.business_return_item_number}</td>
                                                    <td>{item.business_return_item_name}</td>
                                                    <td className="text-center">{item.business_return_qty}</td>
                                                    <td className="text-end">
                                                        {item?.display_item_price}
                                                    </td>
                                                    <td className="text-end">
                                                        {item?.display_vat_amount}
                                                    </td>

                                                    <td className="text-end">
                                                        {item?.display_sub_total_amount}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="cn-bottom-divider" />

                                {/* Totals */}
                                <div className="d-flex justify-content-between align-items-start gap-3 mt-3 pb-4">
                                    <div className="text-uppercase" style={{ fontSize: "11px" }}>
                                        {/* {amountInWords()} */}
                                    </div>

                                    <div></div>

                                    <div style={{ width: "340px", fontSize: "12px" }} className="ms-auto">
                                        <div className="d-flex justify-content-between py-1">
                                            <span className="fw-bold">TAXABLE AMOUNT</span>
                                            <span style={{ width: "120px" }} className="text-end">
                                                {`${formatCurrency(taxableAmount)} AED`}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between py-1">
                                            <span className="fw-bold">VAT AMOUNT</span>
                                            <span style={{ width: "120px" }} className="text-end">
                                                {data?.credit_note?.display_vat_amount}
                                            </span>
                                        </div>

                                        <div className="d-flex justify-content-between py-1">
                                            <span className="fw-bold">TOTAL (Excl.VAT) </span>
                                            <span style={{ width: "120px" }} className="text-end fw-bold">
                                                {data?.credit_note?.display_grand_total_amount}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Signatures */}
                                <div className="d-flex justify-content-between align-items-end gap-3 mt-5">
                                    <div className="text-center" style={{ width: "30%", fontSize: "11px" }}>
                                        <div style={{ borderTop: "2px solid #000", marginBottom: "6px" }} />
                                        <div>RECEIVED BY</div>
                                    </div>

                                    <div className="text-center" style={{ width: "30%", fontSize: "11px" }}>
                                        <div style={{ borderTop: "2px solid #000", marginBottom: "6px" }} />
                                        <div>ACCOUNTS DEPT</div>
                                    </div>

                                    <div className="text-center" style={{ width: "30%", fontSize: "11px" }}>
                                        <div style={{ borderTop: "2px solid #000", marginBottom: "6px" }} />
                                        <div>AUTHORISED SIGNATORY</div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </main>
            }
        </>
    );
};

export default PreviewCreditNote;
